import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { LOCAL_STORAGE_AUTH_LAST_ACTIVITY } from 'features/auth/constants';
import {
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  isIdle,
  markStoredSessionStale,
  readLastActivity,
  writeLastActivity,
} from 'features/auth/utils/lastActivity';
import type { EventsType, IIdleTimer, ITimers } from 'react-idle-timer';

// Only input is activity. A tab hiding, showing or taking focus is not, and
// neither is a resting or bumped mouse, nor a scroll the app makes itself.
const IDLE_EVENTS: readonly EventsType[] = [
  'pointerdown',
  'keydown',
  'touchstart',
  'wheel',
];

const TOUCH_THROTTLE_MS = 1000;

// The timer's pending timeouts with the wall-clock time each is due. A
// timeout stalls while the device sleeps or the browser suspends the tab, and
// then runs after that time.
const pendingTimeouts = new Map<number, { run: () => void; dueAt: number }>();

const idleTimers: ITimers = {
  setTimeout: (run, delay) => {
    const id = window.setTimeout(() => {
      pendingTimeouts.delete(id);
      run();
    }, delay);
    pendingTimeouts.set(id, { run, dueAt: Date.now() + delay });

    return id;
  },
  clearTimeout: id => {
    pendingTimeouts.delete(id);
    window.clearTimeout(id);
  },
  setInterval: (run, delay) => window.setInterval(run, delay),
  clearInterval: id => {
    window.clearInterval(id);
  },
};

// Runs the timeouts due by `now`; true when one ran.
const runOverdueTimeouts = (now: number): boolean => {
  const overdue = [...pendingTimeouts].filter(([, { dueAt }]) => dueAt <= now);

  for (const [id, { run }] of overdue) {
    idleTimers.clearTimeout(id);
    run();
  }

  return overdue.length > 0;
};

// False while the timer has not started.
const isPastDeadline = (
  idleTimer: IIdleTimer | undefined,
  now: number
): boolean => {
  const lastActive = idleTimer?.getLastActiveTime() ?? null;

  return lastActive !== null && isIdle(lastActive.getTime(), now);
};

export type IdleSignOut = {
  start: () => void;
  stop: () => void;
  touch: () => void;
  isIdle: () => boolean;
  warning: boolean;
  remainingMs: () => number;
};

type UseIdleSignOutOptions = {
  onTimeout: () => void;
};

export const useIdleSignOut = ({
  onTimeout,
}: UseIdleSignOutOptions): IdleSignOut => {
  // The timer's own idle flag cannot say whether the session ended: a touch
  // after the deadline ends it without the timer going idle. This ref does.
  const expired = useRef(false);
  const lastTouch = useRef(0);
  // The latest activity time this tab has acted on: its own write to the
  // shared key or another tab's report. A later value in the key is activity
  // in another tab that this tab has not acted on yet.
  const lastSeenActivity = useRef(0);
  const rescue = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timedOut = useRef(onTimeout);
  useEffect(() => {
    timedOut.current = onTimeout;
  }, [onTimeout]);
  const [warning, setWarning] = useState(false);

  const recordActivity = useCallback((at: number) => {
    lastSeenActivity.current = at;
    writeLastActivity(at);
  }, []);

  // A shared-key value is a fresh report from another tab only when it is
  // newer than the latest activity this tab has acted on and still inside
  // the window.
  const isNewerLiveActivity = useCallback(
    (value: number): boolean =>
      value > lastSeenActivity.current && !isIdle(value, Date.now()),
    []
  );

  const clearRescue = useCallback(() => {
    if (rescue.current !== null) {
      clearTimeout(rescue.current);
      rescue.current = null;
    }
  }, []);

  // Ends idle tracking for the page: no timeout, warning or activity write
  // follows, and isIdle() reports true.
  const halt = useCallback(
    (idleTimer: IIdleTimer | undefined) => {
      expired.current = true;
      clearRescue();
      // Pausing unbinds the timer's listeners and clears its timeout, so no
      // callback fires again.
      idleTimer?.pause();
      setWarning(false);
    },
    [clearRescue]
  );

  const expire = useCallback(
    (idleTimer: IIdleTimer | undefined) => {
      if (expired.current) {
        return;
      }

      halt(idleTimer);
      markStoredSessionStale();
      timedOut.current();
    },
    [halt]
  );

  // Another tab was active when the shared key holds a later time than the
  // latest activity this tab has acted on, and that time is still inside the
  // window. Only a missed storage event leaves such a time behind.
  const otherTabActive = useCallback(
    (idleTimer: IIdleTimer | undefined): boolean => {
      const shared = readLastActivity();

      if (shared === null || !isNewerLiveActivity(shared)) {
        return false;
      }

      // One stored value rescues this tab once; a later value is another
      // report of activity.
      lastSeenActivity.current = shared;

      // The library commits its prompt/idle state after this callback
      // returns; the rescue runs after that commit.
      clearRescue();

      rescue.current = setTimeout(() => {
        rescue.current = null;

        if (!expired.current) {
          idleTimer?.activate();
        }
      }, 0);

      return true;
    },
    [clearRescue, isNewerLiveActivity]
  );

  const timeOutUnlessOtherTabActive = useCallback(
    (idleTimer: IIdleTimer | undefined) => {
      if (!otherTabActive(idleTimer)) {
        expire(idleTimer);
      }
    },
    [expire, otherTabActive]
  );

  const timer = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: IDLE_WARNING_MS,
    events: [...IDLE_EVENTS],
    startManually: true,
    eventsThrottle: 1000,
    timers: idleTimers,
    // While the warning is up, input does not extend the session, so it is
    // not recorded as activity either.
    onAction: (_event, idleTimer) => {
      if (idleTimer?.isPrompted()) {
        return;
      }

      const now = Date.now();

      // A touch past the deadline must not publish a fresh timestamp:
      // another tab reading the shared key would see it as still-live.
      if (isPastDeadline(idleTimer, now)) {
        return;
      }

      recordActivity(now);
    },
    onPrompt: (_event, idleTimer) => {
      if (otherTabActive(idleTimer)) {
        return;
      }

      setWarning(true);
    },
    onActive: () => {
      setWarning(false);
    },
    onIdle: (_event, idleTimer) => {
      timeOutUnlessOtherTabActive(idleTimer);
    },
  });
  const timerRef = useRef(timer);
  useEffect(() => {
    timerRef.current = timer;
  });

  // A deferred rescue must not outlive the hook: it would restart a timer
  // the library has already torn down.
  useEffect(() => clearRescue, [clearRescue]);

  // react-idle-timer clears its timeout and unbinds its listeners on every
  // beforeunload, also when the page stays (a "Leave site?" prompt the
  // participant cancels). Once the event is over, a page that is still here
  // re-arms the timer: the cancel counts as activity, except while warning,
  // where the deadline holds, and past the deadline, where the session ends
  // unless another tab was active inside the window.
  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | null = null;

    const rearm = () => {
      pending = null;
      const idleTimer = timerRef.current;
      const last = idleTimer.getLastActiveTime();

      if (expired.current || last === null) {
        return;
      }

      const now = Date.now();

      if (isIdle(last.getTime(), now)) {
        timeOutUnlessOtherTabActive(idleTimer);
        return;
      }

      if (idleTimer.isPrompted()) {
        idleTimer.pause();
        idleTimer.resume();
        return;
      }

      recordActivity(now);
      idleTimer.activate();
    };

    const handleBeforeUnload = () => {
      if (pending !== null) {
        clearTimeout(pending);
      }

      pending = setTimeout(rearm, 0);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      if (pending !== null) {
        clearTimeout(pending);
      }
    };
  }, [recordActivity, timeOutUnlessOtherTabActive]);

  // Activity in another tab restarts this tab's window, so every tab warns
  // and times out on the deadline of the latest activity in any tab. The
  // browser delivers storage events only to the tabs that did not write.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key !== LOCAL_STORAGE_AUTH_LAST_ACTIVITY ||
        event.newValue === null ||
        expired.current
      ) {
        return;
      }

      const at = Number(event.newValue);

      if (
        !Number.isFinite(at) ||
        !isNewerLiveActivity(at) ||
        timerRef.current.getLastActiveTime() === null
      ) {
        return;
      }

      lastSeenActivity.current = at;
      timerRef.current.activate();
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [isNewerLiveActivity]);

  // A tab coming back runs the wall-clock check: its timer may have stalled
  // while it was hidden or the device slept. Past the deadline it times out,
  // unless another tab was active inside the window. Past the warning point
  // it warns, and the warning ends on the deadline.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || expired.current) {
        return;
      }

      const idleTimer = timerRef.current;
      const now = Date.now();

      if (isPastDeadline(idleTimer, now)) {
        timeOutUnlessOtherTabActive(idleTimer);
        return;
      }

      if (
        !idleTimer.isPrompted() &&
        runOverdueTimeouts(now) &&
        idleTimer.isPrompted()
      ) {
        // The timer schedules a late warning's timeout a full warning period
        // ahead; pausing and resuming moves it to the deadline.
        idleTimer.pause();
        idleTimer.resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [timeOutUnlessOtherTabActive]);

  const start = useCallback(() => {
    recordActivity(Date.now());
    timerRef.current.start();
  }, [recordActivity]);

  const stop = useCallback(() => {
    halt(timerRef.current);
  }, [halt]);

  // The warning's button is not a DOM event the timer watches. After the
  // deadline it ends the session unless another tab was active inside the
  // window, as the timer's own handler would, so a stale warning cannot
  // revive it. Inside the window it is throttled like page input.
  const touch = useCallback(() => {
    if (expired.current) {
      return;
    }

    const last = timerRef.current.getLastActiveTime();

    if (last === null) {
      return;
    }

    const now = Date.now();

    if (isIdle(last.getTime(), now)) {
      timeOutUnlessOtherTabActive(timerRef.current);

      return;
    }

    if (now - lastTouch.current < TOUCH_THROTTLE_MS) {
      return;
    }

    lastTouch.current = now;
    recordActivity(now);
    timerRef.current.activate();
  }, [recordActivity, timeOutUnlessOtherTabActive]);

  const isIdleNow = useCallback(
    () => expired.current || timerRef.current.isIdle(),
    []
  );
  const remainingMs = useCallback(
    () => timerRef.current.getRemainingTime(),
    []
  );

  return useMemo(
    () => ({ start, stop, touch, isIdle: isIdleNow, warning, remainingMs }),
    [start, stop, touch, isIdleNow, warning, remainingMs]
  );
};
